import { Loading } from '@components/atomic/atoms';
import OeApiResponseRenderer from '@components/OeApiResponseRenderer';
import { OETrans } from '@components/translation/OETrans';
import { translate } from '@components/translation/translation';
import { IcoMinusCircled, IcoPlus } from '@icons';
import { createTenderCig, deleteTenderCig, editTenderCig, getTenderCigs } from '@lib/api';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

type CigType = {
	id: number | string;
	cig: string;
	lot: string;
	deletable: boolean;
	tenderId: number;
	createdAt?: string;
	createdBy?: string;
	updatedAt?: string;
	updatedBy?: string;
};

interface CigsTabProps {
	tenderId: number | undefined;
}

const CigsTab: React.FC<CigsTabProps> = ({ tenderId }) => {
	const queryClient = useQueryClient();
	const totalRef = useRef<number | undefined>();
	const {
		data: cigsFromApi,
		isPending: isPendingCigsFromApi,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ['cigs', tenderId],
		queryFn: ({ pageParam }) => {
			const queryParams = `?page=${pageParam}`;
			return getTenderCigs(tenderId, queryParams);
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			totalRef.current = lastPage?.meta?.total;
			if (lastPage && lastPage.meta && lastPage.meta.currentPage < lastPage.meta.lastPage) {
				return lastPage.meta.currentPage + 1;
			}
		},
		getPreviousPageParam: (firstPage) => {
			if (firstPage && firstPage.meta && firstPage.meta.currentPage > 1) {
				return firstPage.meta.currentPage - 1;
			}
		},
		enabled: !!tenderId,
	});

	const loadMoreRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			},
			{ threshold: 1 },
		);

		if (loadMoreRef.current) {
			observer.observe(loadMoreRef.current);
		}

		return () => observer.disconnect();
	}, [fetchNextPage, hasNextPage]);

	const [cigs, setCigs] = useState<CigType[]>([]);
	useEffect(() => {
		if (cigsFromApi?.pages) {
			const allCigs = cigsFromApi.pages.flatMap((page) => page.data);
			setCigs(allCigs);
		}
	}, [cigsFromApi]);

	const [responseData, setResponseData] = useState<{ success: boolean; message?: string; data?: any } | null>(null);

	const formRef = useRef<HTMLFormElement>(null);

	const handleOnChange = (id: number | string, field: 'cig' | 'lot', value: string) => {
		const newCigs = [...cigs];
		const index = newCigs.findIndex((cig) => cig.id === id);
		if (index !== -1) {
			newCigs[index][field] = value;
			setCigs(newCigs);
		}
	};

	const addCig = () => {
		const newCig = {
			// used only for react key & distinct between cig creation or modification, real id will be generated by backend
			id: `new-${Date.now()}`,
			cig: '',
			lot: '',
			tenderId,
			deletable: true,
		};
		setCigs([...cigs, newCig]);
	};
	const removeCig = (id: number | string) => {
		setCigs(cigs.filter((cig) => cig.id !== id));
	};

	const addCigMutation = useMutation({
		mutationFn: (cig: Omit<CigType, 'id'>) => {
			return createTenderCig(cig.tenderId, cig);
		},
	});
	const editCigMutation = useMutation({
		mutationFn: (cig: CigType) => {
			return editTenderCig(cig.tenderId, cig);
		},
	});
	const deleteCigMutation = useMutation({
		mutationFn: (cig: CigType) => {
			return deleteTenderCig(cig.tenderId, cig.id as number);
		},
	});

	const handleEdit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setResponseData(null);

		if (cigs.some((cig) => cig.cig === '')) {
			setResponseData({
				success: false,
				message: 'Il campo cig è richiesto',
			});
			return;
		}

		if (cigs.length === 0) {
			setResponseData({
				success: false,
				message: translate('common.error.cigbepresent', 'Deve essere presente almeno un CIG'),
			});
			return;
		}

		const cigNamesSet = new Set<string>(cigs.map((cig) => cig.cig));
		if (cigNamesSet.size !== cigs.length) {
			setResponseData({
				success: false,
				message: translate('common.error.cigunique', 'I CIG devono essere unici'),
			});
			return;
		}

		const cigsToAdd = cigs.filter((cig) => typeof cig.id === 'string' && cig.id.startsWith('new-'));

		const cigsToAddPromises = cigsToAdd.map((cig) => {
			const { id, ...cigData } = cig;
			return addCigMutation.mutateAsync(cigData);
		});

		const cigsToUpdate = cigs
			.filter((cig) => typeof cig.id === 'number' && cig.deletable)
			.filter((cig) => {
				const cigFromApi = cigsFromApi?.pages.flatMap((page) => page.data).find((cigFromApi) => cigFromApi.id === cig.id);
				return cigFromApi && (cig.cig !== cigFromApi.cig || cig.lot !== cigFromApi.lot);
			});

		const cigsToUpdatePromises = cigsToUpdate.map((cig) => {
			const { createdAt, createdBy, updatedAt, updatedBy, ...cigData } = cig;
			return editCigMutation.mutateAsync(cigData);
		});

		const cigsToDelete = cigsFromApi.pages
			.flatMap((page) => page.data)
			.filter((cigFromApi) => !cigs.some((cig) => typeof cig.id === 'number' && cig.id === cigFromApi.id));

		const cigsToDeletePromises = cigsToDelete.map((cig) => {
			return deleteCigMutation.mutateAsync(cig);
		});

		const allPromises = [...cigsToAddPromises, ...cigsToUpdatePromises, ...cigsToDeletePromises];
		const resolvedPromises = await Promise.all(allPromises);

		if (resolvedPromises.some((resolvedPromise) => !resolvedPromise.success)) {
			const failedPromisesData = resolvedPromises
				.filter((resolvedPromise) => !resolvedPromise.success)
				.map((resolvedPromise) => Object.values(resolvedPromise.data).flat());

			setResponseData({
				success: false,
				message:
					// if only 1 unique error message is present show it
					failedPromisesData.length === 1 ? failedPromisesData[0] : translate('common.message.error', 'Si è verificato un errore, riprova'),
				// fallback to show all messages if more than 1 unique error is present
				data: failedPromisesData,
			});

			return;
		}
		queryClient.invalidateQueries({ queryKey: ['cigs', tenderId] });
		setResponseData({
			success: true,
			message: translate('common.message.edited', 'Elemento modificato con successo'),
		});

		setTimeout(() => {
			setResponseData(null);
		}, 3000);
	};

	if (isPendingCigsFromApi) {
		return <Loading fixedHeight="60vh" />;
	}

	return (
		<Form onSubmit={handleEdit} id="form-cig" ref={formRef}>
			<Row style={{ marginBottom: '40px', maxWidth: '100%' }}>
				<Form.Group as={Col}>
					<Form.Label>
						<OETrans code="common.tenders.cig" fallback={'CIG'} />
						&nbsp;<span className="required-field">*</span>
					</Form.Label>
					{cigs.map((cig, index) => {
						return (
							<Row
								key={cig.id}
								style={{
									marginBottom: '40px',
								}}
							>
								<Col xs={6}>
									<Form.Control
										type="text"
										value={cig.cig}
										onChange={(event) => {
											handleOnChange(cig.id, 'cig', event.target.value);
										}}
										disabled={!cig.deletable}
									/>
								</Col>
								<Col xs={4}>
									<Form.Control
										type="text"
										value={cig.lot || ''}
										onChange={(event) => {
											handleOnChange(cig.id, 'lot', event.target.value);
										}}
										disabled={!cig.deletable}
									/>
								</Col>
								<Col xs={2}>
									{totalRef.current && totalRef.current <= cigs.length && index === cigs.length - 1 ? (
										<Button variant="link" className="me-3" onClick={addCig} disabled={cigs.some((cig) => cig.cig === '')}>
											<IcoPlus className="second-color ico-small indicators" />
										</Button>
									) : null}
									{cigs.length > 1 && cig.deletable ? (
										<Button
											variant="link"
											onClick={() => {
												removeCig(cig.id);
											}}
										>
											<IcoMinusCircled className="red-color ico-small indicators" />
										</Button>
									) : null}
								</Col>
							</Row>
						);
					})}
					<div ref={loadMoreRef} style={{ height: '20px' }} />
					{isFetchingNextPage ? <Loading fixedHeight="50px" /> : null}
				</Form.Group>
			</Row>
			{!!responseData ? <OeApiResponseRenderer dataToHandle={responseData} /> : null}
		</Form>
	);
};

export default CigsTab;
